import { css, StyleDeclarationValue } from 'aphrodite';
import { Slot } from 'radix-ui';
import { ComponentProps, ForwardRefExoticComponent, forwardRef } from 'react';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ChevronDown } from '../../components/svgs/icons/ChevronDown';
import { s } from './styles';

export const Button: ForwardRefExoticComponent<
	AphroditeStyled<
		ComponentProps<'button'> & {
			variant?:
				| 'primary'
				| 'secondary'
				| 'outline'
				| 'success'
				| 'link'
				| 'destructive'
				| 'destructive-secondary'
				| 'ghost';
			size?: 'default' | 'xs' | 'sm' | 'lg' | 'icon';
			isLoading?: boolean;
			styles?: StyleDeclarationValue[];
			asChild?: boolean;
		}
	> &
		React.RefAttributes<HTMLButtonElement> & { selectorClassName?: string }
> = forwardRef(
	(
		{ variant = 'primary', asChild, size = 'default', children, isLoading, styles = [], selectorClassName, ...props },
		ref
	) => {
		const Comp = asChild ? Slot.Root : 'button';
		const disabled = props.disabled || isLoading;
		const spinnerOverlaysContent = isLoading && !asChild;
		const renderChildren = () => {
			if (spinnerOverlaysContent) {
				return (
					<>
						<span className={css(s.loadingContent)} data-slot='button-content'>
							{children}
						</span>
						<span className={css(s.loadingOverlay)}>
							<LoadingSpinner type='tiny' />
						</span>
					</>
				);
			}
			return isLoading ? <LoadingSpinner type='tiny' /> : children;
		};
		return (
			<Comp
				className={`${css(s.base, s[variant], s[size], spinnerOverlaysContent && s.loading, ...styles)} ${
					selectorClassName ? selectorClassName : ''
				}`}
				ref={ref}
				data-slot='button'
				{...props}
				disabled={disabled}
			>
				{renderChildren()}
			</Comp>
		);
	}
);

Button.displayName = 'Button';

export const SelectButton: ForwardRefExoticComponent<
	AphroditeStyled<
		ComponentProps<'button'> & {
			size?: 'default' | 'sm';
			styles?: StyleDeclarationValue[];
			asChild?: boolean;
		}
	> &
		React.RefAttributes<HTMLButtonElement>
> = forwardRef(({ asChild, size = 'default', children, styles = [], ...props }, ref) => {
	const Comp = asChild ? Slot.Root : 'button';
	return (
		<Comp className={`${css(s.selectButton, s[size], ...styles)}`} ref={ref} {...props}>
			<span style={{ pointerEvents: 'none' }}>{children}</span>
			<ChevronDown className={css(s.selectButtonChevron)} />
		</Comp>
	);
});

SelectButton.displayName = 'SelectButton';
